export function first_defined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return null;
}

function get_manual_form_id_keys() {
  const form_id_keys = ['form_id', 'survey_form_id', 'survey_form', 'form'];
  return form_id_keys;
}

const MANUAL_FORM_ID_READERS = [
  { key: 'form_id', read: (item) => item.form_id },
  { key: 'survey_form_id', read: (item) => item.survey_form_id },
  { key: 'survey_form', read: (item) => item.survey_form },
  { key: 'form', read: (item) => item.form },
];

function get_manual_form_id_value(item = {}, key) {
  const reader = MANUAL_FORM_ID_READERS.find((entry) => entry.key === key);
  if (!reader) return null;
  return reader.read(item);
}

function get_first_present_key(item = {}, keys = []) {
  const found_key = keys.find((key) => get_manual_form_id_value(item, key) != null) || null;
  return found_key;
}

function build_assigned_onboarding_ids(item = {}) {
  const assigned_ids = Array.isArray(item?.assigned_onboarding_form_ids)
    ? item.assigned_onboarding_form_ids.map((value) => String(value))
    : [];
  return assigned_ids;
}

export function normalize_manual_form(item) {
  if (!item || typeof item !== 'object') {
    const normalized_item = null;
    return normalized_item;
  }

  const found_form_id_key = get_first_present_key(item, get_manual_form_id_keys());
  const form_id = found_form_id_key ? get_manual_form_id_value(item, found_form_id_key) : null;
  const manual_verification_form_id = first_defined(item.manual_verification_form_id, item.id);
  const normalized_item = {
    ...item,
    form_id,
    manual_verification_form_id,
    assigned_onboarding_form_ids: build_assigned_onboarding_ids(item),
  };
  return normalized_item;
}

function resolve_onboarding_form_id(item = {}) {
  const onboarding_form_id = first_defined(item.id, item.form_id, item.onboarding_form_id);
  return onboarding_form_id;
}

function build_onboarding_form_name(item = {}) {
  const form_name = item.form_name || item.name || 'Untitled Form';
  return form_name;
}

function build_onboarding_form_description(item = {}) {
  const form_description = item.form_description || item.description || 'No description provided.';
  return form_description;
}

export function normalize_onboarding_form(item) {
  if (!item || typeof item !== 'object') {
    const normalized_item = null;
    return normalized_item;
  }

  const id = resolve_onboarding_form_id(item);
  if (id == null) {
    const missing_id_value = null;
    return missing_id_value;
  }

  const normalized_item = {
    ...item,
    id,
    form_id: first_defined(item.form_id, item.id),
    form_name: build_onboarding_form_name(item),
    form_description: build_onboarding_form_description(item),
  };
  return normalized_item;
}

function resolve_manual_link_ids(item = {}) {
  const ids = {
    onboarding_form_id: first_defined(item.onboarding_form_id, item.onboarding_form),
    manual_verification_form_id: first_defined(item.manual_verification_form_id, item.manual_verification_form),
  };
  return ids;
}

function has_manual_link_ids(link_ids = {}) {
  const has_ids = link_ids.onboarding_form_id != null && link_ids.manual_verification_form_id != null;
  return has_ids;
}

function is_object(value) {
  return Boolean(value && typeof value === 'object');
}

export function normalize_manual_link(item) {
  const link_ids = get_valid_manual_link_ids(item);
  if (!link_ids) return null;
  return build_normalized_manual_link(item, link_ids);
}

function get_valid_manual_link_ids(item) {
  if (!is_object(item)) return null;
  const link_ids = resolve_manual_link_ids(item);
  return has_manual_link_ids(link_ids) ? link_ids : null;
}

function build_normalized_manual_link(item, link_ids) {
  const normalized_link = {
    ...item,
    form_link_id: first_defined(item.form_link_id, item.id),
    onboarding_form_id: link_ids.onboarding_form_id,
    manual_verification_form_id: link_ids.manual_verification_form_id,
    onboarding_form_name: item.onboarding_form_name || '',
    manual_verification_form_name: item.manual_verification_form_name || '',
    is_active: item.is_active !== false,
  };
  return normalized_link;
}

export function parse_manual_verification_response(response, parse_json_if_string) {
  const data = get_response_data(response);
  const parsed = parse_json_if_string(data);
  return is_object(parsed) ? parsed : {};
}

function get_response_data(response) {
  if (response?.data != null) return response.data;
  return {};
}

export function get_response_items(parsed = {}, ...keys) {
  const values = keys.map((key) => get_response_item_value(parsed, key));
  const items = first_defined(...values, []);
  return Array.isArray(items) ? items : [];
}

const RESPONSE_ITEM_READERS = [
  { key: 'manual_verification_forms', read: (parsed) => parsed.manual_verification_forms },
  { key: 'forms', read: (parsed) => parsed.forms },
  { key: 'results', read: (parsed) => parsed.results },
  { key: 'data', read: (parsed) => parsed.data },
  { key: 'links', read: (parsed) => parsed.links },
  { key: 'onboarding_forms', read: (parsed) => parsed.onboarding_forms },
];

function get_response_item_value(parsed = {}, key) {
  const reader = RESPONSE_ITEM_READERS.find((entry) => entry.key === key);
  if (!reader) return undefined;
  return reader.read(parsed);
}

export function build_onboarding_links(links = []) {
  const onboarding_links = (Array.isArray(links) ? links : []).reduce((acc, link) => {
    if (link?.is_active === false) {
      return acc;
    }
    acc[String(link.onboarding_form_id)] = true;
    return acc;
  }, {});
  return onboarding_links;
}

export function get_assigned_onboarding_ids(links = []) {
  const assigned_ids = (Array.isArray(links) ? links : [])
    .filter((link) => link?.is_active !== false)
    .map((link) => String(link.onboarding_form_id));
  return assigned_ids;
}

export function merge_assigned_onboarding_ids(current_ids = [], onboarding_form_id, assigned) {
  const normalized_ids = Array.isArray(current_ids) ? current_ids.map((value) => String(value)) : [];
  const target_id = String(onboarding_form_id);
  const next_ids = assigned
    ? Array.from(new Set([...normalized_ids, target_id]))
    : normalized_ids.filter((value) => value !== target_id);
  return next_ids;
}

export function replace_assigned_onboarding_ids(assigned_ids = []) {
  const next_ids = Array.isArray(assigned_ids) ? assigned_ids.map((value) => String(value)) : [];
  return next_ids;
}

export function get_first_assigned_onboarding_form_value(form, onboarding_forms = []) {
  const assigned_ids = get_assigned_form_ids(form);
  if (!assigned_ids.length) return null;

  return (onboarding_forms || []).find((item) => has_assigned_form_id(assigned_ids, item)) || null;
}

function get_assigned_form_ids(form) {
  if (!Array.isArray(form?.assigned_onboarding_form_ids)) return [];
  return form.assigned_onboarding_form_ids.map((value) => String(value));
}

function has_assigned_form_id(assigned_ids, item) {
  return assigned_ids.includes(String(item?.id)) || assigned_ids.includes(String(item?.form_id));
}

export function build_mapping_route_query(form = {}, {
  manual_verification_form_id,
  manual_form_builder_id,
  active_link,
  active_onboarding_form,
} = {}) {
  const query = {
    manual_verification_form_id: String(manual_verification_form_id),
    manual_verification_form_name: String(form?.form_name || ''),
  };

  add_query_value(query, 'manual_verification_builder_form_id', manual_form_builder_id);
  add_query_value(query, 'onboarding_form_id', active_onboarding_form?.id);
  add_query_value(query, 'onboarding_form_name', active_onboarding_form?.form_name || active_link?.onboarding_form_name);
  add_query_value(query, 'form_link_id', first_defined(active_link?.form_link_id, active_link?.id));
  return query;
}

function add_query_value(query, key, value) {
  if (value == null || value === '') return;
  const text = String(value);
  const setter = QUERY_VALUE_SETTERS.find((entry) => entry.key === key);
  if (setter) setter.write(query, text);
}

const QUERY_VALUE_SETTERS = [
  {
    key: 'manual_verification_builder_form_id',
    write: (query, text) => {
      query.manual_verification_builder_form_id = text;
    },
  },
  {
    key: 'onboarding_form_id',
    write: (query, text) => {
      query.onboarding_form_id = text;
    },
  },
  {
    key: 'onboarding_form_name',
    write: (query, text) => {
      query.onboarding_form_name = text;
    },
  },
  {
    key: 'form_link_id',
    write: (query, text) => {
      query.form_link_id = text;
    },
  },
];
